// src/App.jsx

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './App.css';

// Screens
import Login from './components/Auth/Login';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import AdminLayout from './components/Admin/Layout/AdminLayout';
import UserList from './components/Admin/Users/UserList';
import ProgrammingWorkspace from './components/Admin/Programming/ProgrammingWorkspace';
import PlaybackMonitor from './components/Admin/Monitoring/PlaybackMonitor';
import RoleManagement from './components/Admin/Roles/RoleManagement';
import HomepageBuilder from './components/Admin/Website/HomepageBuilder';
import AboutPageBuilder from './components/Admin/Website/AboutPageBuilder';
import ServicesPageBuilder from './components/Admin/Website/ServicesPageBuilder';
import ApplicationsPageBuilder from './components/Admin/Website/ApplicationsPageBuilder';
import ApplicationsCatalogManager from './components/Admin/Website/ApplicationsCatalogManager';
import BioPagesManager from './components/Admin/Website/BioPagesManager';
import PartnerPagesManager from './components/Admin/Website/PartnerPagesManager';
import MediaLibrary from './components/Admin/Website/MediaLibrary';
import BroadcasterDashboard from './components/Broadcast/BroadcasterDashboard';
import RumblePlayer from './components/Public/RumblePlayer';
import NowPlaying from './components/Public/NowPlaying';
import PublicChannel from './components/Public/PublicChannel';
import PublicHomepage from './components/Public/PublicHomepage';
import PublicServices from './components/Public/PublicServices';
import PublicApplications from './components/Public/PublicApplications';

const LoadingScreen = ({ message = 'Loading...' }) => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-soft)',
      padding: '2rem',
    }}
  >
    <div className="card" style={{ minWidth: 260, textAlign: 'center' }}>
      {message}
    </div>
  </div>
);

const HeraldRedirect = () => {
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.location.assign('/herald');
    }
  }, []);

  return <LoadingScreen message="Opening The Kingdom Herald..." />;
};

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;

  if (requireAdmin) {
    const isAdmin = user.roles?.some((role) => ['super-admin', 'admin'].includes(role.slug));
    if (!isAdmin) return <LoadingScreen message="You don't have permission to view this area." />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <LoadingScreen />;
  if (user) {
    const isAdmin = user.roles?.some((role) => ['super-admin', 'admin'].includes(role.slug));
    return <Navigate to={isAdmin ? '/admin' : '/dashboard'} replace />;
  }

  return children;
};

const AdminDashboard = () => {
  const { user, isSuperAdmin } = useAuth();
  const canManageSiteFlags = isSuperAdmin();
  const calendarEmbedUrl = process.env.REACT_APP_PUBLIC_CALENDAR_EMBED || '';
  const [applicationsMaintenanceEnabled, setApplicationsMaintenanceEnabled] = React.useState(false);
  const [maintenanceLoading, setMaintenanceLoading] = React.useState(false);
  const [maintenanceSaving, setMaintenanceSaving] = React.useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = React.useState('');
  const [maintenanceError, setMaintenanceError] = React.useState('');
  const cards = [
    { label: 'Active users', value: '84', detail: '+12% vs last week' },
    { label: 'Provisioned apps', value: '11', detail: '3 rolling out' },
    { label: 'Avg. satisfaction', value: '4.8/5', detail: 'Support SLA · 1h' },
  ];

  React.useEffect(() => {
    if (!canManageSiteFlags) return;

    let mounted = true;
    const loadFlags = async () => {
      setMaintenanceLoading(true);
      try {
        const { data } = await axios.get('/admin/site-flags');
        if (!mounted) return;
        setApplicationsMaintenanceEnabled(Boolean(data?.flags?.applications_maintenance_enabled));
        setMaintenanceError('');
      } catch (error) {
        if (!mounted) return;
        setMaintenanceError(error.response?.data?.message || 'Unable to load site flags.');
      } finally {
        if (mounted) setMaintenanceLoading(false);
      }
    };

    loadFlags();
    return () => {
      mounted = false;
    };
  }, [canManageSiteFlags]);

  const toggleApplicationsMaintenance = async () => {
    if (!canManageSiteFlags) return;

    const nextValue = !applicationsMaintenanceEnabled;
    setMaintenanceSaving(true);
    setMaintenanceMessage('');
    setMaintenanceError('');
    try {
      const { data } = await axios.put('/admin/site-flags', {
        applications_maintenance_enabled: nextValue,
      });
      const enabled = Boolean(data?.flags?.applications_maintenance_enabled);
      setApplicationsMaintenanceEnabled(enabled);
      setMaintenanceMessage(
        enabled
          ? 'Applications page maintenance mode is enabled.'
          : 'Applications page maintenance mode is disabled.'
      );
    } catch (error) {
      setMaintenanceError(error.response?.data?.message || 'Unable to update site flags.');
    } finally {
      setMaintenanceSaving(false);
    }
  };

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '1.6rem' }}>
      <div className="card">
        <h2 style={{ fontSize: '1.8rem', marginBottom: '0.4rem' }}>
          Welcome back, {user?.name ?? 'team lead'} 👋
        </h2>
        <p className="text-muted">
          Monitor engagement, manage permissions, and keep every workspace deployment healthy.
        </p>
      </div>

      <div className="card-grid">
        {cards.map((card) => (
          <div key={card.label} className="card">
            <p className="text-muted" style={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
              {card.label}
            </p>
            <h3 style={{ fontSize: '2.7rem', margin: '0.4rem 0' }}>{card.value}</h3>
            <p className="text-muted">{card.detail}</p>
          </div>
        ))}
      </div>

      {canManageSiteFlags && (
        <div className="card">
          <h3 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>Applications Page Maintenance</h3>
          <p className="text-muted" style={{ marginBottom: '0.9rem' }}>
            When enabled, public users cannot view the applications list page.
          </p>
          {maintenanceLoading ? (
            <p className="text-muted">Loading maintenance status...</p>
          ) : (
            <>
              <p className="text-muted" style={{ marginBottom: '0.8rem' }}>
                Current status:{' '}
                <strong>{applicationsMaintenanceEnabled ? 'Enabled' : 'Disabled'}</strong>
              </p>
              <button
                type="button"
                className={applicationsMaintenanceEnabled ? 'btn btn-muted' : 'btn btn-danger'}
                onClick={toggleApplicationsMaintenance}
                disabled={maintenanceSaving}
              >
                {maintenanceSaving
                  ? 'Saving...'
                  : applicationsMaintenanceEnabled
                    ? 'Disable maintenance'
                    : 'Enable maintenance'}
              </button>
            </>
          )}
          {maintenanceMessage && (
            <div className="notice notice-success" style={{ marginTop: '0.9rem' }}>
              {maintenanceMessage}
            </div>
          )}
          {maintenanceError && (
            <div className="notice notice-danger" style={{ marginTop: '0.9rem' }}>
              {maintenanceError}
            </div>
          )}
        </div>
      )}

      <div className="card">
        <h3 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>Phase 1 delivery</h3>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.5rem' }}>
          {[
            'Authentication + onboarding',
            'Role-based access control',
            'User management workspace',
            'Admin shell & telemetry',
          ].map((item) => (
            <li key={item} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span role="img" aria-hidden="true">
                ✅
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-muted" style={{ marginTop: '1rem' }}>
          Next up: app provisioning, automated audits, and personalized dashboards.
        </p>
      </div>

      {calendarEmbedUrl && (
        <div className="card">
          <h3 style={{ fontSize: '1.25rem', marginBottom: '0.75rem' }}>Public calendar</h3>
          <div className="admin-calendar-embed">
            <iframe
              src={calendarEmbedUrl}
              title="Public calendar"
              frameBorder="0"
              scrolling="no"
              style={{ width: '100%', height: '100%', border: '0' }}
            />
          </div>
        </div>
      )}
    </section>
  );
};

function App() {
  // PUBLIC_URL is also populated from package.json's "homepage" during local
  // development. Only apply that base when the current URL is actually under it,
  // so both localhost/login and the production /app/login route work.
  const publicBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/';
  const basename =
    publicBase && (pathname === publicBase || pathname.startsWith(`${publicBase}/`))
      ? publicBase
      : '/';

  return (
    <BrowserRouter basename={basename}>
      <AuthProvider>
        <Routes>
          <Route path="/watch" element={<RumblePlayer />} />
          <Route path="/tv" element={<NowPlaying />} />
          <Route path="/now-playing" element={<NowPlaying />} />
          <Route path="/channel" element={<PublicChannel />} />
          <Route path="/about-us" element={<PublicHomepage layoutKey="about-us" activePage="about" />} />
          <Route path="/services" element={<PublicServices />} />
          <Route path="/applications" element={<PublicApplications />} />
          <Route path="/herald" element={<HeraldRedirect />} />

          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            }
          />

          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<UserList />} />
            <Route path="roles" element={<RoleManagement />} />
            <Route path="homepage" element={<HomepageBuilder />} />
            <Route path="about-page" element={<AboutPageBuilder />} />
            <Route path="services-page" element={<ServicesPageBuilder />} />
            <Route path="applications-page" element={<ApplicationsPageBuilder />} />
            <Route path="applications-catalog" element={<ApplicationsCatalogManager />} />
            <Route path="bios" element={<BioPagesManager />} />
            <Route path="partners" element={<PartnerPagesManager />} />
            <Route path="media-library" element={<MediaLibrary />} />
            <Route path="programming" element={<ProgrammingWorkspace defaultTab="schedule" />} />
            <Route path="program-library" element={<ProgrammingWorkspace defaultTab="library" />} />
            <Route path="playback-monitor" element={<PlaybackMonitor />} />
          </Route>

          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <BroadcasterDashboard />
              </ProtectedRoute>
            }
          />

          <Route path="/" element={<PublicHomepage layoutKey="homepage" activePage="home" />} />
          <Route path="*" element={<LoadingScreen message="This page doesn't exist." />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
